import { Box } from "@mui/material";
import { NavbarFilters } from "@qubit/autoparts";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import NavSearchInput from "~/features/navbar/NavSearchInput";

import { useDebounce } from "~/hooks/useDebounce";
import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";
import { useGetOrderTypesQuery } from "~/redux/warehouse/orders.hooks";

import {
  setOrderFilter,
  setOrderStatusFilters,
  setOrderTypeFilters
} from "./orders.slice";

type BinOrProductResult = {
  type: string;
  variantId?: Guid;
  binId?: Guid;
  displayText: string;
};

const defaultOrderStatuses = [
  "batched",
  "received",
  "picked",
  "canceled",
  "verified",
  "staged",
  "stalled",
  "loaded"
] as const;

export type OrderStatus = (typeof defaultOrderStatuses)[number];

function buildOrderStatusFilter(orderStatuses: string[] | null | undefined) {
  return (orderStatuses || defaultOrderStatuses) as OrderStatus[];
}

export function OrdersSearch() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const fulfillmentCenter = useAppSelector(selectUsersFulfillmentCenter);
  const [textInput, setTextInput] = useState<string | null>(null);
  const orderStatusFilters = useAppSelector(
    (state) => state.ordersSlice.orderStatusFilters
  );
  const orderTypeFilters = useAppSelector(
    (state) => state.ordersSlice.orderTypeFilters
  );

  const { data: orderTypes = [] } = useGetOrderTypesQuery();

  const debouncedSearchText = useDebounce(textInput, 300);

  const statusMenuItems = buildOrderStatusFilter(
    fulfillmentCenter?.clientConfiguration?.orderStatuses
  ).map((option) => ({
    filter: option,
    translation: t(option)
  }));

  const orderTypeMenuItems = orderTypes.map((ot) => ({
    filter: ot.orderType,
    translation: ot.orderType
  }));

  const hasOrderFilters =
    orderStatusFilters.length > 0 || orderTypeFilters.length > 0;

  const handleSearchOrdersSubmit = useCallback(
    (searchText: string) => {
      dispatch(setOrderFilter(searchText));
    },
    [dispatch]
  );

  const handleSearchClear = useCallback(() => {
    dispatch(setOrderFilter(""));
  }, [dispatch]);

  const onResetFilters = () => {
    dispatch(setOrderStatusFilters([]));
    dispatch(setOrderTypeFilters([]));
    handleSearchClear();
  };

  useEffect(() => {
    if (debouncedSearchText && debouncedSearchText.length >= 3) {
      handleSearchOrdersSubmit(debouncedSearchText);
    } else if (!debouncedSearchText) {
      handleSearchClear();
    }
  }, [debouncedSearchText, handleSearchClear, handleSearchOrdersSubmit]);

  return (
    <Box
      display="flex"
      flexGrow="1"
      role="search"
      aria-label="Orders search and filters"
    >
      <NavSearchInput<BinOrProductResult>
        textInput={textInput || ""}
        setTextInput={setTextInput}
        searchPlaceholder={t("search orders")}
        onLeaveInputTextShown
        clearCb={handleSearchClear}
      />
      <NavbarFilters
        menuItemsTitle={t("order status")}
        menuItemList={statusMenuItems}
        selectedMenuItems={orderStatusFilters}
        onFilterData={(statuses: string[]) =>
          dispatch(setOrderStatusFilters(statuses))
        }
        secondaryMenuItemList={
          orderTypeMenuItems.length > 0 ? orderTypeMenuItems : undefined
        }
        secondaryMenuItemsTitle={t("order type")}
        selectedSecondaryMenuItems={orderTypeFilters}
        onSecondaryFilterData={(types: string[]) =>
          dispatch(setOrderTypeFilters(types))
        }
        onResetFilters={onResetFilters}
        hasActiveFilters={hasOrderFilters}
      />
    </Box>
  );
}
